import { isPendingInvitationClient } from "../../invitations/invitedClientUiGuards";
import {
  INVITATION_STATE,
  normalizeInvitationState,
} from "../../invitations/invitationIdentity";
import { sortClientNutritionPlans } from "../../Firestore/nutrition/nutritionRecords";

const toNonNegativeInteger = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.floor(parsed));
};

const toTimestampMs = (value) => {
  if (value && typeof value.toMillis === "function") {
    const ms = value.toMillis();
    return Number.isFinite(ms) ? ms : 0;
  }

  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms : 0;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  return 0;
};

const toNormalizedId = (value) => String(value ?? "").trim().toLowerCase();
const toTrimmedString = (value) => String(value ?? "").trim();

const buildAcceptedInvitationIdSet = (invitationSummaries = []) => {
  if (!Array.isArray(invitationSummaries) || invitationSummaries.length === 0) {
    return new Set();
  }

  const acceptedInvitationIds = new Set();
  invitationSummaries.forEach((summary) => {
    const invitationId = toNormalizedId(summary?.invitationId);
    if (!invitationId) return;
    if (normalizeInvitationState(summary?.state) !== INVITATION_STATE.ACCEPTED) {
      return;
    }
    acceptedInvitationIds.add(invitationId);
  });

  return acceptedInvitationIds;
};

const buildMatchedPendingUserIdSet = (
  clients = [],
  acceptedInvitationIds = new Set(),
) => {
  if (!Array.isArray(clients) || clients.length === 0) {
    return new Set();
  }

  const matchedPendingUserIds = new Set();
  clients.forEach((client) => {
    if (!isPendingInvitationClient(client)) return;
    const invitationId = toNormalizedId(client?.invitationId);
    if (invitationId && acceptedInvitationIds.has(invitationId)) return;
    const pendingUserId = toNormalizedId(client?.pendingUserId);
    if (!pendingUserId) return;
    matchedPendingUserIds.add(pendingUserId);
  });

  return matchedPendingUserIds;
};

export const selectPendingNutritionPlanEntries = ({
  pendingWorkspaceSummaries = [],
  clients = [],
  invitationSummaries = [],
} = {}) => {
  if (
    !Array.isArray(pendingWorkspaceSummaries) ||
    pendingWorkspaceSummaries.length === 0
  ) {
    return [];
  }

  const acceptedInvitationIds = buildAcceptedInvitationIdSet(invitationSummaries);
  const matchedPendingUserIds = buildMatchedPendingUserIdSet(
    clients,
    acceptedInvitationIds,
  );

  return pendingWorkspaceSummaries
    .map((summary, index) => {
      const pendingUserId = toTrimmedString(summary?.pendingUserId);
      const normalizedPendingUserId = toNormalizedId(pendingUserId);
      const nutritionPlansCount = toNonNegativeInteger(summary?.nutritionPlansCount);
      const nutritionPlans = Array.isArray(summary?.nutritionPlans)
        ? summary.nutritionPlans
        : [];

      return {
        id: pendingUserId,
        pendingUserId,
        normalizedPendingUserId,
        invitationId: toTrimmedString(summary?.invitationId),
        invitationEmail: toTrimmedString(summary?.invitationEmail),
        nutritionPlansCount,
        nutritionPlans,
        timestampMs: toTimestampMs(summary?.lastUpdateMs),
        fallbackIndex: Number.isFinite(Number(summary?.fallbackIndex))
          ? Number(summary.fallbackIndex)
          : index,
      };
    })
    .filter((entry) => {
      if (!entry.pendingUserId) return false;
      if (entry.nutritionPlansCount <= 0) return false;
      if (acceptedInvitationIds.has(toNormalizedId(entry?.invitationId))) {
        return true;
      }
      return !matchedPendingUserIds.has(entry.normalizedPendingUserId);
    })
    .sort((left, right) => {
      if (left.timestampMs !== right.timestampMs) {
        return right.timestampMs - left.timestampMs;
      }
      return left.fallbackIndex - right.fallbackIndex;
    });
};

export const getPendingNutritionPlansTotalCount = (entries = []) => {
  if (!Array.isArray(entries) || entries.length === 0) return 0;
  return entries.reduce(
    (total, entry) => total + toNonNegativeInteger(entry?.nutritionPlansCount),
    0,
  );
};

export const flattenPendingNutritionPlansFromEntries = (entries = []) => {
  if (!Array.isArray(entries) || entries.length === 0) {
    return [];
  }

  const flattened = [];

  entries.forEach((entry) => {
    const pendingUserId = toTrimmedString(entry?.pendingUserId);
    if (!pendingUserId) return;

    const sortedPlans = sortClientNutritionPlans(
      Array.isArray(entry?.nutritionPlans) ? entry.nutritionPlans : [],
    );

    sortedPlans.forEach((planRecord, index) => {
      const planId = toTrimmedString(planRecord?.id);
      if (!planId) return;

      flattened.push({
        id: `${pendingUserId}:${planId}`,
        pendingUserId,
        invitationId: toTrimmedString(entry?.invitationId),
        invitationEmail: toTrimmedString(entry?.invitationEmail),
        timestampMs: Number(entry?.timestampMs) || 0,
        workspaceFallbackIndex: Number(entry?.fallbackIndex) || 0,
        planId,
        planRecord,
        fallbackIndex: index,
      });
    });
  });

  return flattened;
};

export const shouldShowPendingNutritionPlansSection = (entries = []) =>
  Array.isArray(entries) && entries.length > 0;

export const removePendingNutritionPlanFromWorkspaceSummaries = ({
  pendingWorkspaceSummaries = [],
  pendingUserId,
  planId,
} = {}) => {
  if (
    !Array.isArray(pendingWorkspaceSummaries) ||
    pendingWorkspaceSummaries.length === 0
  ) {
    return [];
  }

  const normalizedPendingUserId = toNormalizedId(pendingUserId);
  const normalizedPlanId = toNormalizedId(planId);
  if (!normalizedPendingUserId || !normalizedPlanId) {
    return pendingWorkspaceSummaries;
  }

  return pendingWorkspaceSummaries
    .map((summary) => {
      const summaryPendingUserId = toNormalizedId(summary?.pendingUserId);
      if (summaryPendingUserId !== normalizedPendingUserId) {
        return summary;
      }

      const nutritionPlans = Array.isArray(summary?.nutritionPlans)
        ? summary.nutritionPlans
        : [];
      const nextNutritionPlans = nutritionPlans.filter((planRecord) => {
        const summaryPlanId = toNormalizedId(planRecord?.id);
        return summaryPlanId !== normalizedPlanId;
      });

      if (nextNutritionPlans.length === nutritionPlans.length) {
        return summary;
      }

      return {
        ...summary,
        nutritionPlans: sortClientNutritionPlans(nextNutritionPlans),
        nutritionPlansCount: nextNutritionPlans.length,
      };
    })
    .filter((summary) => {
      const blocksCount = toNonNegativeInteger(summary?.blocksCount);
      const nutritionPlansCount = toNonNegativeInteger(summary?.nutritionPlansCount);
      return blocksCount > 0 || nutritionPlansCount > 0;
    });
};
